// Главная Yolutap — по design/reference/Home*.dc.html (RU/AZ/EN).
//
// Тексты — из design/content/{ru,az,en}.json (один источник на три языка) и
// из kept-copy.json: FAQ, отзывы, форма связи, гайд, меню входа и вопросы
// мини-теста перенесены с прежней главной (public/methodology*.html) как
// есть. Цены и обещания — только из PRODUCT.md.
//
// Мини-тест — НАСТОЯЩИЙ: 4 вопроса и 4 архетипа прежней главной (решение
// владельца 05.10.2026), в результате два верхних архетипа с полосками по
// числу ответов и честная пометка «подсказка, а не профиль». Вопросы из
// мокапа — заглушки, их здесь нет.
//
// Страниц, которых ещё нет, касаются мягкие заглушки: «Учёба за рубежом»
// ведёт в живой модуль (STUDY_ABROAD_HREF), школьные продукты — к форме связи,
// репетиторы — «скоро» без ссылки.
import { animate, inView, scroll, stagger } from "motion";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import ru from "../../../design/content/ru.json";
import az from "../../../design/content/az.json";
import en from "../../../design/content/en.json";
import kept from "./kept-copy.json";
import { TurnstileFallback, TurnstileWidget, type TurnstileHandle } from "@/components/security/TurnstileWidget";
import { AUTH_ERR, authErrorText, readAuthError } from "@/lib/auth-errors";
import { reviewTextLang } from "@/lib/reviews-order";
import { SiteFooter, SiteHeader, STUDY_ABROAD_HREF, TUTORS_JOIN_HREF, TUTORS_MAP_HREF } from "@/components/site/SiteChrome";
import { ABROAD_COPY, EXAMPLE_MARK, HOME_ABROAD_TEASER } from "@/components/abroad/abroad-copy";
import { ABROAD_ENTRY_COPY, ABROAD_GUEST_START_PATH } from "@/edu/guest";
import { HomeDoors } from "./HomeDoors";
import { HomePins } from "./HomePins";
import { ABOUT_COPY } from "./pins-copy";
import { ev } from "./home-events";
import { GoogleReviewsBadge } from "@/components/site/GoogleReviewsBadge";
import { withCountries } from "@/edu/countries-count";
import { HomePopular, type PopularData } from "./HomePopular";
import { SCHOOLS_PATH } from "@/components/schools/schools-copy";

/**
 * Первый экран: "pins" — карта с пинами (ТЗ 25.10.2026, HomePins.tsx) и
 * короткая страница под ней (PinsBelow); "doors" — три двери (ТЗ 09.10.2026,
 * HomeDoors.tsx) с прежней длинной страницей; "legacy" — прежний hero
 * (LegacyHero ниже). ОТКАТ — заменить "pins" на "doors" в этой строке;
 * больше ничего трогать не нужно.
 */
export const HOME_HERO = "pins" as "pins" | "doors" | "legacy";

export type HomeLang = "ru" | "az" | "en";
type Copy = typeof ru;
const COPY: Record<HomeLang, Copy> = { ru, az: az as Copy, en: en as Copy };

const PRE: Record<HomeLang, string> = { ru: "", az: "/az", en: "/en" };
const SAMPLE_PDF: Record<HomeLang, string> = {
  ru: "/samples/sample-16-18-ru-leader-communicator-v1.pdf",
  az: "/sample-report.pdf",
  en: "/samples/sample-18-plus-en-investigative-social-artistic-v1.pdf",
};
const REV_LANG_NOTE: Record<HomeLang, Partial<Record<HomeLang, string>>> = {
  ru: { az: "Отзыв на азербайджанском", en: "Отзыв на английском" },
  az: { ru: "Rəy rus dilindədir", en: "Rəy ingilis dilindədir" },
  en: { ru: "Review in Russian", az: "Review in Azerbaijani" },
};
const GUIDE_ERR: Record<HomeLang, { email: string; fail: string; net: string; robot: string }> = {
  ru: { email: "Введите корректный email.", fail: "Не удалось отправить, попробуйте ещё раз.", net: "Ошибка сети. Попробуйте ещё раз.", robot: "Подтвердите, что вы не робот." },
  az: { email: "Zəhmət olmasa düzgün email daxil edin.", fail: "Xəta baş verdi, yenidən cəhd edin.", net: "Şəbəkə xətası. Yenidən cəhd edin.", robot: "Robot olmadığınızı təsdiq edin." },
  en: { email: "Please enter a valid email.", fail: "Something went wrong, please try again.", net: "Network error. Please try again.", robot: "Please confirm you are not a robot." },
};
// Пока отправка ждёт токен капчи (виджет стартует по первому касанию формы).
// AZ — на вычитку носителем.
const CHECKING: Record<HomeLang, string> = { ru: "Проверяем…", az: "Yoxlanılır…", en: "Checking…" };
// Гайд для родителей после заявки — сразу ссылкой на PDF своего языка
// (public/guides/). Письма с гайдом нет: раньше форма обещала «придёт на
// почту», а отправки не существовало. AZ — на вычитку носителем.
const GUIDE_READY: Record<HomeLang, { before: string; link: string }> = {
  ru: { before: "Спасибо! Гайд готов — ", link: "скачать PDF" },
  az: { before: "Təşəkkürlər! Bələdçi hazırdır — ", link: "PDF-i yükləyin" },
  en: { before: "Thank you! Your guide is ready — ", link: "download the PDF" },
};
const guidePdf = (lang: HomeLang) => `/guides/yolutap-guide-parents-${lang}.pdf`;
const QUOTE = "«Empower our learners with valuable insights as they plan their future journeys.»";
const EAS = "European Azerbaijan School";

// FAQ главной — шесть вопросов (решение владельца 05.10.2026), в этом порядке:
// платно ли, как получу отчёт, для какого возраста, насколько точно,
// безопасны ли данные, на каких языках. Номера — индексы в kept-copy.json
// (порядок одинаков в трёх языках). JSON-LD FAQPage берёт те же вопросы.
export const HOME_FAQ = [5, 7, 2, 6, 9, 10] as const;

// Знак EAS и название школы текстом рядом: круговая надпись знака мелка при
// любом разумном размере (так же было в #342). alt — на языке страницы (16.10.2026).
const EAS_ALT: Record<HomeLang, string> = {
  az: `${EAS} loqosu`,
  ru: `Логотип ${EAS}`,
  en: `${EAS} logo`,
};
function EasMark({ size = 40, lang }: { size?: number; lang: HomeLang }) {
  return (
    <img
      src="/partners/eas/eas-logo-h56.webp"
      srcSet="/partners/eas/eas-logo-h56.webp 1x, /partners/eas/eas-logo-h112.webp 2x"
      alt={EAS_ALT[lang]}
      width={Math.round((size * 47) / 56)}
      height={size}
      loading="lazy"
      decoding="async"
      style={{ display: "block", flex: "0 0 auto" }}
    />
  );
}

type Code = "creator" | "explorer" | "mentor" | "organizer";
const CODE_COLOR: Record<Code, string> = { creator: "#7A5AAE", explorer: "#1E5A6B", mentor: "#2F7D63", organizer: "#8A5A12" };

const reduced = () => {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return true;
  }
};

// ─── картинки ─────────────────────────────────────────────────────────────
const PHOTOS = {
  "gen-hero-teen": [640, 960, 1136],
  "gen-family-kitchen": [640, 960, 1264],
  "eas-teacher-help": [640, 960, 1280, 1600],
  "eas-class-wide": [640, 960, 1280, 1600, 2000],
  "eas-test-intro-screen": [640, 960, 1280, 1500],
  "eas-test-question-screen": [640, 960, 1280, 1500],
  "eas-class-teacher": [640, 960, 1280, 1600],
} as const;
type PhotoName = keyof typeof PHOTOS;

function Photo({ name, alt, sizes, eager, style }: { name: PhotoName; alt: string; sizes: string; eager?: boolean; style?: React.CSSProperties }) {
  const ws = PHOTOS[name];
  return (
    <picture>
      <source type="image/webp" srcSet={ws.map((w) => `/photos/home/${name}-${w}.webp ${w}w`).join(", ")} sizes={sizes} />
      <img
        src={`/photos/home/${name}.jpg`}
        alt={alt}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        fetchPriority={eager ? "high" : undefined}
        style={style}
      />
    </picture>
  );
}

const Arrow = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

// ─── бесплатная проверка ──────────────────────────────────────────────────
function shuffle<T>(a: T[]): T[] {
  const b = a.slice();
  for (let i = b.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [b[i], b[j]] = [b[j], b[i]];
  }
  return b;
}

function FreeCheck({ lang, t, stepOnly = false }: { lang: HomeLang; t: Copy; stepOnly?: boolean }) {
  const data = kept[lang].mini;
  const [orders, setOrders] = useState(() => data.q.map((q) => q.opts));
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Code[]>([]);
  const prev = useRef(0);
  const qRef = useRef<HTMLDivElement>(null);
  const resRef = useRef<HTMLDivElement>(null);
  const barRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const started = useRef(false);
  const done = step >= data.q.length;

  // Порядок вариантов перемешивается как на прежней главной — только в браузере,
  // чтобы серверная разметка совпадала с первой отрисовкой.
  useEffect(() => setOrders(data.q.map((q) => shuffle(q.opts))), [data]);

  useEffect(() => {
    const dir = step > prev.current ? 1 : -1;
    const was = prev.current;
    prev.current = step;
    if (was === step) return;
    try {
      if (!done && qRef.current) {
        animate(qRef.current, reduced() ? { opacity: [0.4, 1] } : { opacity: [0, 1], x: [14 * dir, 0] }, { duration: 0.26, ease: [0.22, 1, 0.36, 1] });
        qRef.current.querySelector<HTMLButtonElement>(".hp-opt")?.focus({ preventScroll: true });
      }
      if (done) {
        if (resRef.current) {
          animate(resRef.current, { opacity: [0, 1] }, { duration: 0.3 });
          resRef.current.focus({ preventScroll: true });
        }
        const bars = barRefs.current.filter(Boolean) as HTMLSpanElement[];
        if (bars.length && !reduced()) animate(bars, { scaleX: [0, 1] }, { type: "spring", stiffness: 260, damping: 30, delay: stagger(0.06, { startDelay: 0.1 }) });
      }
    } catch {
      /* анимация — украшение */
    }
  }, [step, done]);

  const pick = (code: Code) => {
    if (!started.current) {
      started.current = true;
      ev("mini_test_start");
    }
    const next = answers.slice(0, step).concat(code);
    setAnswers(next);
    setStep(step + 1);
    if (step + 1 >= data.q.length) {
      const top = rank(next)[0];
      ev("mini_test_complete", { direction: top.code });
    }
  };

  const ranked = done ? rank(answers) : [];
  const shown = ranked.filter((r) => r.n > 0);
  const title = shown.length > 1
    ? `${data.results[shown[0].code].title} + ${data.results[shown[1].code].title.toLowerCase()}`
    : shown[0] ? data.results[shown[0].code].title : "";
  const q = data.q[Math.min(step, data.q.length - 1)];
  barRefs.current = [];

  return (
    // Мини-тест — без автоперевода браузера (src/lib/no-translate.ts): Chrome ломал варианты ответов.
    <div id="check" className="hp-check" data-check translate="no">
      {!done ? (
        <>
          <div className="hp-check__top">
            {/* На главной заголовок «Pulsuz yoxlama» уже над блоком — внутри только счётчик шагов (27.10.2026). */}
            <span>{stepOnly ? t.free_check_n_of_4.replace("{{ stepLabel }}", String(step + 1)).split(" · ").pop() : t.free_check_n_of_4.replace("{{ stepLabel }}", String(step + 1))}</span>
            {step > 0 && (
              <button type="button" className="hp-plain" onClick={() => { setStep(step - 1); setAnswers(answers.slice(0, step - 1)); }}>
                {t.back}
              </button>
            )}
          </div>
          <span className="hp-check__track" aria-hidden="true">
            <span className="hp-check__fill" style={{ transform: `scaleX(${step / data.q.length})` }} />
          </span>
          <div className="hp-check__q" ref={qRef} role="group" aria-label={q.prompt}>
            <p>{q.prompt}</p>
            {orders[Math.min(step, data.q.length - 1)].map((o) => (
              <button key={o.code + o.text} type="button" className="hp-opt" onClick={() => pick(o.code as Code)}>
                {o.text}
              </button>
            ))}
          </div>
        </>
      ) : (
        <div className="hp-check__res" ref={resRef} tabIndex={-1} aria-live="polite">
          <span className="hp-check__small">{t.first_signal_from_4_answers}</span>
          <p className="hp-check__title">{title}</p>
          <div className="hp-bars">
            {shown.map((r, i) => (
              <div className="hp-bar" key={r.code}>
                <span>{data.results[r.code].title}</span>
                <span className="hp-bar__track">
                  <span
                    ref={(el) => { barRefs.current[i] = el; }}
                    className="hp-bar__fill"
                    style={{ width: `${(r.n / data.q.length) * 100}%`, background: CODE_COLOR[r.code] }}
                  />
                </span>
              </div>
            ))}
          </div>
          <p className="hp-check__note">{t.this_is_a_hint_not_a_profile_the_full_test_adjus}</p>
          <div className="hp-row" style={{ gap: 10 }}>
            <a className="hp-btn" href={`/start?lang=${lang}&pkg=basic`} data-cta="mini_test_cta_full">
              {t.full_report_35}
            </a>
            <button type="button" className="hp-plain" onClick={() => { setStep(0); setAnswers([]); }}>
              {t.start_over}
            </button>
          </div>
          {/* Вход в бесплатный подбор вузов — результат мини-теста был тупиком,
              кроме платного теста (аудит 12.10). */}
          <a
            className="hp-check__abroad"
            href={ABROAD_GUEST_START_PATH[lang]}
            data-cta="mini_test_to_abroad"
          >
            {ABROAD_ENTRY_COPY[lang]}
          </a>
        </div>
      )}
    </div>
  );
}

function rank(answers: Code[]): { code: Code; n: number }[] {
  const codes: Code[] = ["creator", "explorer", "mentor", "organizer"];
  const n = (c: Code) => answers.filter((a) => a === c).length;
  // При равенстве выше тот, кого выбрали раньше.
  const first = (c: Code) => {
    const i = answers.indexOf(c);
    return i < 0 ? 99 : i;
  };
  return codes.map((c) => ({ code: c, n: n(c) })).sort((a, b) => b.n - a.n || first(a.code) - first(b.code));
}

// ─── отзывы ───────────────────────────────────────────────────────────────
type Review = { id: string; name: string; child_age: string | null; text: string; rating: number; text_lang?: HomeLang };

function Reviews({ lang }: { lang: HomeLang }) {
  const r = kept[lang].reviews;
  const [list, setList] = useState<Review[]>([]);
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [token, setToken] = useState<string | null>(null);
  const [tsKey, setTsKey] = useState(0);
  const [err, setErr] = useState("");
  const [capDown, setCapDown] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const form = useRef<HTMLFormElement>(null);
  const ts = useRef<TurnstileHandle>(null);

  useEffect(() => {
    fetch(`/api/reviews?lang=${lang}`)
      .then((x) => (x.ok ? x.json() : null))
      .then((d) => d?.reviews && setList(d.reviews))
      .catch(() => {});
  }, [lang]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const f = new FormData(form.current!);
    const name = String(f.get("name") ?? "").trim();
    const text = String(f.get("text") ?? "").trim();
    const age = String(f.get("age") ?? "").trim();
    if (!rating) return setErr(r.errRating);
    if (text.length < 10) return setErr(r.errShort);
    setErr("");
    setBusy(true);
    // Проверка стартует при первом касании формы и могла не успеть: ждём
    // токен, а не отказываем. Кнопка заранее не блокируется.
    if (!token) setWaiting(true);
    const tok = token ?? (await ts.current?.waitForToken()) ?? null;
    setWaiting(false);
    if (!tok) {
      setBusy(false);
      return setErr(r.errRobot);
    }
    try {
      const res = await fetch("/api/reviews", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name, text, child_age: age, rating, lang, turnstile_token: tok }),
      });
      const d = await res.json();
      if (d.ok) setSent(true);
      else {
        // Сервер не смог проверить капчу (siteverify недоступен) — вместо
        // «попробуйте ещё раз» прямые контакты.
        setCapDown(d.reason === "unavailable");
        setErr(d.reason === "unavailable" ? "" : d.error || r.errGeneric);
        setToken(null);
        setTsKey((k) => k + 1);
      }
    } catch {
      setErr(r.errNet);
      setToken(null);
      setTsKey((k) => k + 1);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="hp-wrap hp-section" id="reviews" aria-labelledby={list.length ? "hp-rev-title" : undefined}>
      <div className="hp-reviews__head">
        <div className="hp-reviews__title">
          {list.length > 0 && (
            <h2 className="hp-h2" id="hp-rev-title">
              {r.title}
            </h2>
          )}
          <GoogleReviewsBadge lang={lang} placement="home_reviews" />
        </div>
        {!open && (
          <button type="button" className="hp-btn hp-btn--outline" onClick={() => setOpen(true)}>
            {r.open}
          </button>
        )}
      </div>
      {list.length > 0 && (
        <div className="hp-revgrid">
          {list.map((rv) => {
            const tl = rv.text_lang ?? reviewTextLang(rv.text);
            const note = REV_LANG_NOTE[lang][tl];
            return (
              <div className="hp-rev" key={rv.id}>
                <div className="hp-rev__stars" aria-label={`${rv.rating}/5`}>
                  {"★".repeat(rv.rating)}
                  {"☆".repeat(5 - rv.rating)}
                </div>
                <p className="hp-rev__text" lang={tl}>
                  «{rv.text}»
                </p>
                {note && <p className="hp-rev__lang">{note}</p>}
                <div className="hp-rev__author">
                  {rv.name}
                  {rv.child_age ? ` · ${rv.child_age}` : ""}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {open && (
        <div className="hp-revform">
          {sent ? (
            <div className="hp-thanks" role="status" dangerouslySetInnerHTML={{ __html: r.thanks }} />
          ) : (
            <form ref={form} className="hp-form" onSubmit={submit} noValidate>
              <h3>{r.formTitle}</h3>
              <div className="hp-field">
                <span className="hp-label" id="hp-rev-rating">
                  {r.rating}
                </span>
                <div className="hp-stars" role="group" aria-labelledby="hp-rev-rating">
                  {r.starLabels.map((lbl, i) => (
                    <button key={lbl} type="button" className="hp-star" aria-label={lbl} aria-pressed={rating > i} onClick={() => setRating(i + 1)}>
                      ★
                    </button>
                  ))}
                </div>
              </div>
              <div className="hp-fields2">
                <div className="hp-field">
                  <label htmlFor="hp-rev-name">
                    {r.name} <span className="hp-req">*</span>
                  </label>
                  <input id="hp-rev-name" name="name" required placeholder={r.namePh} maxLength={80} />
                </div>
                <div className="hp-field">
                  <label htmlFor="hp-rev-age">{r.age}</label>
                  <input id="hp-rev-age" name="age" placeholder={r.agePh} maxLength={40} />
                </div>
              </div>
              <div className="hp-field">
                <label htmlFor="hp-rev-text">
                  {r.text} <span className="hp-req">*</span>
                </label>
                <textarea id="hp-rev-text" name="text" required placeholder={r.textPh} maxLength={2000} />
              </div>
              <TurnstileWidget key={tsKey} lang={lang} startOn={form} handleRef={ts} onToken={setToken} onExpired={() => setToken(null)} onError={() => setToken(null)} />
              {capDown && <TurnstileFallback lang={lang} />}
              {err && (
                <p className="hp-err" role="alert">
                  {err}
                </p>
              )}
              <div className="hp-row">
                <button type="submit" className="hp-btn" disabled={busy}>
                  {waiting ? CHECKING[lang] : busy ? r.sending : r.submit}
                </button>
                <button type="button" className="hp-plain" onClick={() => setOpen(false)}>
                  {r.cancel}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </section>
  );
}

// ─── форма связи ──────────────────────────────────────────────────────────
/** Режим формы на странице «Школам» (30.10.2026): свой заголовок, поля «Школа» и «Должность», пометка source=school. */
export type ContactSchool = { title: string; lead: string; schoolL: string; schoolPh: string; positionL: string; positionPh: string };

/** Форма связи главной. С school — та же форма для страницы «Школам»: тема не выбирается (школа = «Школа / партнёрство»). */
export function Contact({ lang, school }: { lang: HomeLang; school?: ContactSchool }) {
  const c = kept[lang].contact;
  const [token, setToken] = useState<string | null>(null);
  const [tsKey, setTsKey] = useState(0);
  const [err, setErr] = useState("");
  const [capDown, setCapDown] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const form = useRef<HTMLFormElement>(null);
  const ts = useRef<TurnstileHandle>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const f = new FormData(form.current!);
    const message = String(f.get("message") ?? "").trim();
    if (message.length < 5) return setErr(c.errShort);
    setErr("");
    setBusy(true);
    // Проверка стартует при первом касании формы и могла не успеть: ждём
    // токен, а не отказываем. Кнопка заранее не блокируется.
    if (!token) setWaiting(true);
    const tok = token ?? (await ts.current?.waitForToken()) ?? null;
    setWaiting(false);
    if (!tok) {
      setBusy(false);
      return setErr(c.errRobot);
    }
    try {
      const res = await fetch("/api/feedback", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          name: String(f.get("name") ?? "").trim(),
          email: String(f.get("email") ?? "").trim(),
          // Школа — та же категория, что у темы «Школа / партнёрство» в форме главной.
          category: school ? "suggestion" : (c.topics[Number(f.get("topic") ?? 0)]?.v ?? "other"),
          message,
          ...(school
            ? { source: "school", school: String(f.get("school") ?? "").trim(), position: String(f.get("position") ?? "").trim() }
            : {}),
          turnstile_token: tok,
        }),
      });
      const d = await res.json();
      if (d.ok) setSent(true);
      else {
        setCapDown(d.reason === "unavailable");
        setErr(d.reason === "unavailable" ? "" : d.error || c.errGeneric);
        setToken(null);
        setTsKey((k) => k + 1);
      }
    } catch {
      setErr(c.errNet);
      setToken(null);
      setTsKey((k) => k + 1);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="hp-wrap hp-section hp-contact" id="contact" style={{ scrollMarginTop: 80 }}>
      <div className="hp-contact__copy">
        {school ? <h2 className="hp-h2">{school.title}</h2> : <h2 className="hp-h2" dangerouslySetInnerHTML={{ __html: c.title }} />}
        <p className="hp-lead">{school ? school.lead : c.lead}</p>
        <div className="hp-contact__rows">
          <div>
            <span>{c.rows[0]}</span>
            <a href="mailto:support@yolutap.com">support@yolutap.com</a>
          </div>
          <div>
            <span>{c.rows[1]}</span>
            <a href="https://wa.me/994513933197">+994 51 393 31 97</a>
          </div>
        </div>
      </div>
      <div className="hp-contact__form">
        {sent ? (
          <div className="hp-thanks" role="status" dangerouslySetInnerHTML={{ __html: c.thanks }} />
        ) : (
          <form ref={form} className="hp-form" onSubmit={submit} noValidate>
            <div className="hp-fields2">
              <div className="hp-field">
                <label htmlFor="hp-c-name">{c.nameL}</label>
                <input id="hp-c-name" name="name" placeholder={c.namePh} maxLength={80} autoComplete="name" />
              </div>
              <div className="hp-field">
                <label htmlFor="hp-c-email">{c.emailL}</label>
                <input id="hp-c-email" name="email" type="email" placeholder="you@example.com" maxLength={255} autoComplete="email" />
              </div>
            </div>
            {school ? (
              <div className="hp-fields2">
                <div className="hp-field">
                  <label htmlFor="hp-c-school">{school.schoolL}</label>
                  <input id="hp-c-school" name="school" placeholder={school.schoolPh} maxLength={120} autoComplete="organization" />
                </div>
                <div className="hp-field">
                  <label htmlFor="hp-c-position">{school.positionL}</label>
                  <input id="hp-c-position" name="position" placeholder={school.positionPh} maxLength={80} autoComplete="organization-title" />
                </div>
              </div>
            ) : (
              <div className="hp-field">
                <label htmlFor="hp-c-topic">{c.topicL}</label>
                <select id="hp-c-topic" name="topic" defaultValue="0">
                  {c.topics.map((o, i) => (
                    <option key={i} value={String(i)}>
                      {o.t}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="hp-field">
              <label htmlFor="hp-c-msg">{c.msgL}</label>
              <textarea id="hp-c-msg" name="message" required minLength={5} maxLength={2000} placeholder={c.msgPh} />
            </div>
            <TurnstileWidget key={tsKey} lang={lang} startOn={form} handleRef={ts} onToken={setToken} onExpired={() => setToken(null)} onError={() => setToken(null)} />
            {capDown && <TurnstileFallback lang={lang} />}
            {err && (
              <p className="hp-err" role="alert">
                {err}
              </p>
            )}
            <div>
              <button type="submit" className="hp-btn" disabled={busy}>
                {waiting ? CHECKING[lang] : busy ? c.sending : c.submit} <Arrow />
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

// ─── гайд за email ────────────────────────────────────────────────────────
function Guide({ lang }: { lang: HomeLang }) {
  const g = kept[lang].guide;
  const e = GUIDE_ERR[lang];
  const [token, setToken] = useState<string | null>(null);
  const [tsKey, setTsKey] = useState(0);
  const [err, setErr] = useState("");
  const [capDown, setCapDown] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const form = useRef<HTMLFormElement>(null);
  const ts = useRef<TurnstileHandle>(null);

  const submit = async (ev2: React.FormEvent) => {
    ev2.preventDefault();
    const f = new FormData(form.current!);
    const email = String(f.get("email") ?? "").trim();
    const company = String(f.get("hp_x") ?? "").trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setErr(e.email);
    setErr("");
    setBusy(true);
    // Проверка стартует при первом касании формы и могла не успеть: ждём
    // токен, а не отказываем. Кнопка заранее не блокируется.
    if (!token) setWaiting(true);
    const tok = token ?? (await ts.current?.waitForToken()) ?? null;
    setWaiting(false);
    if (!tok) {
      setBusy(false);
      return setErr(e.robot);
    }
    try {
      const res = await fetch("/api/lead", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, source: "lead_magnet", lang, company, turnstile_token: tok }),
      });
      const d = await res.json().catch(() => null);
      if (d?.ok) {
        setSent(true);
        try {
          const w = window as unknown as { gtag?: (...a: unknown[]) => void; fbq?: (...a: unknown[]) => void };
          w.gtag?.("event", "lead_capture", { source: "lead_magnet" });
          w.fbq?.("track", "Lead", { content_name: "parents_guide" });
        } catch {
          /* ignore */
        }
      } else {
        // Токен одноразовый: после любого отказа нужен новый.
        setCapDown(d?.reason === "unavailable");
        setErr(d?.reason === "unavailable" ? "" : e.fail);
        setToken(null);
        setTsKey((k) => k + 1);
      }
    } catch {
      setErr(e.net);
      setToken(null);
      setTsKey((k) => k + 1);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="hp-wrap" id="guide" style={{ paddingTop: 24, paddingBottom: 24 }}>
      <div className="hp-guide">
        <div className="hp-guide__copy">
          <h2 dangerouslySetInnerHTML={{ __html: g.title }} />
          <p className="hp-lead">{g.sub}</p>
        </div>
        {sent ? (
          <div className="hp-thanks" role="status">
            <span>
              {GUIDE_READY[lang].before}
              <a href={guidePdf(lang)} target="_blank" rel="noopener" data-cta="lead_guide_pdf">
                {GUIDE_READY[lang].link}
              </a>
            </span>
          </div>
        ) : (
          <form ref={form} className="hp-guide__form" onSubmit={submit} noValidate>
            {/* Honeypot. Не «company»: такое поле браузер заполнял организацией человека (03.11.2026). */}
            <div className="hp-hp" aria-hidden="true">
              <label htmlFor="hp-guide-hp-x">Leave this empty</label>
              <input id="hp-guide-hp-x" type="text" name="hp_x" tabIndex={-1} autoComplete="off" data-1p-ignore="" data-lpignore="true" data-bwignore="" data-form-type="other" />
            </div>
            <input type="email" name="email" required placeholder={g.ph} maxLength={255} autoComplete="email" aria-label="Email" />
            <button type="submit" className="hp-btn hp-btn--lg" disabled={busy} data-cta="lead_guide">
              {waiting ? CHECKING[lang] : g.submit} <Arrow />
            </button>
            <div style={{ flexBasis: "100%" }}>
              <TurnstileWidget key={tsKey} lang={lang} startOn={form} handleRef={ts} onToken={setToken} onExpired={() => setToken(null)} onError={() => setToken(null)} />
              {capDown && <TurnstileFallback lang={lang} />}
            </div>
            {err && (
              <p className="hp-err" role="alert" style={{ flexBasis: "100%" }}>
                {err}
              </p>
            )}
          </form>
        )}
      </div>
    </section>
  );
}

// ─── баннер ошибки входа (Supabase кладёт её в адрес) ─────────────────────
function AuthErrorBar({ lang }: { lang: HomeLang }) {
  const [msg, setMsg] = useState<{ text: string; detail: string | null } | null>(null);
  useEffect(() => {
    try {
      const { code, error } = readAuthError(window.location.href);
      if (!code && !error) return;
      setMsg({ text: authErrorText(lang, code, error), detail: code || error });
      history.replaceState(null, "", location.pathname + location.search);
    } catch {
      /* ignore */
    }
  }, [lang]);
  if (!msg) return null;
  const t = AUTH_ERR[lang];
  return (
    <div className="hp-authbar" role="alert">
      <div className="hp-authbar__in">
        <p>
          {msg.text}
          {msg.detail && <small>{msg.detail}</small>}
        </p>
        <a className="hp-btn" href="/cabinet">
          {t.cta}
        </a>
        <button type="button" className="hp-plain" aria-label={t.dismiss} onClick={() => setMsg(null)} style={{ color: "#9B2C2C", fontSize: 24, minWidth: 44 }}>
          ×
        </button>
      </div>
    </div>
  );
}

// ─── прежний первый экран (до 09.10.2026) ─────────────────────────────────
// Не рендерится (HOME_HERO = "doors"), оставлен для отката одной строкой.
// Разметка — ровно та, что стояла на главной.
function LegacyHero({ lang, t, start, photoRef }: { lang: HomeLang; t: Copy; start: string; photoRef: React.RefObject<HTMLDivElement | null> }) {
  return (
    <section id="top" className="hp-wrap hp-hero">
      <div className="hp-hero__copy">
        <h1>
          {t.not_knowing_what_to_become_at_14_is_normal}{" "}
          <span className="hp-serif">{t.what_matters_is_noticing_in_time_what_draws_them}</span>
        </h1>
        <p className="hp-hero__lead">{t.a_science_based_test_and_a_10_page_personal_repo}</p>
        <div className="hp-row">
          <a href={`${start}&pkg=basic`} className="hp-btn hp-btn--lg" data-cta="hero_start">
            {t.take_the_test_35} <Arrow />
          </a>
          <a href="#check" className="hp-textbtn" data-cta="hero_check">
            {t.or_a_free_one_minute_check}
          </a>
        </div>
        <a href={STUDY_ABROAD_HREF[lang]} className="hp-hero__abroad" data-cta="hero_abroad">
          {HOME_ABROAD_TEASER[lang]}
        </a>
        <div className="hp-proof">{t.this_year_grades_7_10_at_european_azerbaijan_sch}</div>
      </div>
      <div className="hp-hero__media">
        <div className="hp-hero__photo" ref={photoRef}>
          <Photo name="gen-hero-teen" alt={t.a_high_school_student_looks_thoughtfully_at_her_} sizes="(max-width: 880px) 100vw, 50vw" eager />
        </div>
        <FreeCheck lang={lang} t={t} />
      </div>
    </section>
  );
}

// ─── страница ─────────────────────────────────────────────────────────────
// ─── цены и FAQ — общие для обеих раскладок ──────────────────────────────
type PriceRow = { color: string; name: string; who: string; price: string; act: string; href: string; cta: string; more?: { label: string; href: string } };

function PricesSection({ t, prices, more }: { t: Copy; prices: PriceRow[]; more: (m?: { label: string; href: string }) => ReactNode }) {
  return (
    <>
    {/* цены */}
    <section id="prices" className="hp-wrap hp-section hp-prices">
      <h2 className="hp-h2">{t.all_prices_on_one_page}</h2>
      <div className="hp-pcards">
        {prices.map((p) => (
          <div className="hp-pcard" key={p.cta}>
            <span className="hp-pcard__name">
              <span className="hp-dot" style={{ background: p.color }} />
              {p.name}
            </span>
            <span className="hp-pcard__who">{p.who}</span>
            {more(p.more)}
            <span className="hp-pcard__row">
              <b>{p.price}</b>
              <a className="hp-pcard__act" href={p.href} data-cta={p.cta}>
                {p.act} →
              </a>
            </span>
          </div>
        ))}
      </div>
      <div className="hp-table-wrap">
        <table className="hp-table">
          <thead>
            <tr>
              <th scope="col">{t.product}</th>
              <th scope="col">{t.for_whom}</th>
              <th scope="col">{t.price}</th>
              <th scope="col"><span className="hp-sr">{t.action}</span></th>
            </tr>
          </thead>
          <tbody>
            {prices.map((p) => (
              <tr key={p.cta}>
                <th scope="row">
                  <span className="hp-dot" style={{ background: p.color }} />
                  {p.name}
                  {more(p.more)}
                </th>
                <td>{p.who}</td>
                <td className="hp-price">{p.price}</td>
                <td className="hp-act">
                  <a href={p.href} data-cta={p.cta}>{p.act}</a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
    </>
  );
}

function FaqSection({ k }: { k: (typeof kept)[HomeLang] }) {
  return (
    <>
    {/* FAQ */}
    <section id="faq" className="hp-wrap hp-section hp-faq" style={{ paddingTop: 48 }}>
      <h2 className="hp-h2">{k.faqTitle}</h2>
      <div className="hp-faq__list">
        {HOME_FAQ.map((i) => k.faq[i]).map((f) => (
          <details key={f.q}>
            <summary>{f.q}</summary>
            <p className="hp-faq__a" dangerouslySetInnerHTML={{ __html: f.a }} />
          </details>
        ))}
      </div>
    </section>
    </>
  );
}

/** Школам — одна строка-ссылка после отзывов (27.10.2026); с 30.10.2026 ведёт на страницу «Школам». */
const SCHOOLS_LINK: Record<HomeLang, string> = { az: "Məktəblərə →", ru: "Школам →", en: "For schools →" };
function SchoolsLink({ lang }: { lang: HomeLang }) {
  return (
    <div id="schools" className="hp-wrap hp-schools-link">
      <a href={SCHOOLS_PATH[lang]} className="hp-textbtn" data-cta="schools_link">{SCHOOLS_LINK[lang]}</a>
    </div>
  );
}

// ─── под картой с пинами: «Yolutap nədir?», проверка, цены, FAQ, связь ─────
// Прежние блоки сокращены (ТЗ 25.10.2026): остались якоря, на которые ведут
// шапка, реклама и другие страницы — #check (?mini=1, /test), #prices, #contact.
function PinsAbout({ lang, countries }: { lang: HomeLang; countries: number | null }) {
  const a = ABOUT_COPY[lang];
  const href: Record<string, string | undefined> = {
    career: `/start?lang=${lang}&pkg=basic`,
    tutors: TUTORS_MAP_HREF[lang],
    abroad: STUDY_ABROAD_HREF[lang],
    tutor_join: TUTORS_JOIN_HREF[lang],
  };
  return (
    <section id="about" className="hp-wrap pa" aria-labelledby="pa-title">
      <h2 id="pa-title" className="hp-h2">{a.title}</h2>
      <p className="hp-lead">{a.lead}</p>
      <ul className="pa-list">
        {a.items.map((it) => (
          <li key={it.pin}>
            <h3>{href[it.pin] ? <a href={href[it.pin]} data-cta={`about_${it.pin}`}>{it.title}</a> : it.title}</h3>
            <p>{withCountries(it.text, lang, countries)}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function HomePage({
  lang,
  countries = null,
  popular = null,
  hero = HOME_HERO,
}: {
  lang: HomeLang;
  countries?: number | null;
  /** «Populyar» перед FAQ (29.10.2026): страницы предметов и статьи из loader. */
  popular?: PopularData | null;
  /** Только для проверок: раскладка, которую держат проверки дверей (откат). */
  hero?: typeof HOME_HERO;
}) {
  const t = COPY[lang];
  const k = kept[lang];
  const pre = PRE[lang];
  const start = `/start?lang=${lang}`;
  const heroPhoto = useRef<HTMLDivElement>(null);
  const grid = useRef<HTMLDivElement>(null);
  const band = useRef<HTMLElement>(null);
  const bandImg = useRef<HTMLDivElement>(null);

  // Анимация — по DESIGN.md › Motion; всё видно и без JS.
  useEffect(() => {
    if (reduced()) return;
    const ease = [0.22, 1, 0.36, 1] as const;
    const stops: (() => void)[] = [];
    try {
      if (heroPhoto.current) animate(heroPhoto.current, { clipPath: ["inset(7% 7% 7% 7% round 28px)", "inset(0% 0% 0% 0% round 28px)"] }, { duration: 0.95, ease });
      const check = document.querySelector<HTMLElement>("[data-check]");
      if (check && window.matchMedia("(min-width: 561px)").matches) animate(check, { opacity: [0, 1], y: [28, 0] }, { duration: 0.6, delay: 0.35, ease });
      if (band.current && bandImg.current) stops.push(scroll(animate(bandImg.current, { y: ["-4%", "4%"] }, { ease: "linear" }), { target: band.current, offset: ["start end", "end start"] }));
      if (grid.current) {
        const cards = grid.current.querySelectorAll("[data-prod]");
        stops.push(inView(grid.current, () => { animate(cards, { opacity: [0.001, 1], y: [16, 0] }, { duration: 0.5, ease, delay: stagger(0.06) }); }, { amount: 0.15 }));
      }
    } catch {
      /* анимация — украшение */
    }
    return () => stops.forEach((s) => s?.());
  }, []);

  // cta_click по [data-cta] и прямой заход на мини-тест (?mini=1, #mini-test,
  // #check — путь рекламы и /test) — как делал бутстрап прежней главной.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const el = (e.target as HTMLElement | null)?.closest?.("[data-cta]");
      if (el) ev("cta_click", { cta_id: el.getAttribute("data-cta") || "" });
    };
    document.addEventListener("click", onClick);
    try {
      const q = new URLSearchParams(location.search);
      if (q.get("mini") === "1" || location.hash === "#mini-test" || location.hash === "#check") {
        ev("mini_test_open", { entry: "url" });
        const el = document.getElementById("check");
        el?.scrollIntoView({ block: "center" });
        el?.querySelector<HTMLButtonElement>(".hp-opt")?.focus({ preventScroll: true });
      }
    } catch {
      /* ignore */
    }
    return () => document.removeEventListener("click", onClick);
  }, []);

  const prices = useMemo(
    () => [
      { color: "#1E5A6B", name: t.career_profile, who: t.families_ages_10_22_2, price: "35 ₼", act: t.take_the_test_2, href: `${start}&pkg=basic`, cta: "price_basic", more: { label: t.sample_report, href: SAMPLE_PDF[lang] } },
      { color: "#7A5AAE", name: t.profile_coach_session, who: t.families_child_with_parent, price: "250 ₼", act: t.choose, href: `${start}&pkg=premium`, cta: "price_premium", more: { label: t.who_runs_the_sessions, href: `${pre}/coaches` } },
      { color: "#2F7D63", name: t.study_abroad, who: t.families_grades_10_11_2, price: t.free, act: ABROAD_COPY[lang].cta, href: STUDY_ABROAD_HREF[lang], cta: "price_abroad" },
      { color: "#F0A63A", name: t.career_guidance_for_classes, who: t.schools_2, price: t["25_student"], act: t.discuss, href: SCHOOLS_PATH[lang], cta: "price_school_career" },
      { color: "#4BAE8C", name: t.school_climate, who: t.schools_grades_4_11, price: t["8_per_student_a_year_first_round_free"], act: t.start, href: SCHOOLS_PATH[lang], cta: "price_climate" },
    ],
    [t, lang, pre, start],
  );

  const more = (m?: { label: string; href: string }): ReactNode =>
    m ? (
      <span className="hp-more">
        <a href={m.href}>{m.label}</a>
      </span>
    ) : null;

  // Блоки прежней главной — общие для обеих раскладок (с 25.10.2026 карта с
  // пинами возвращает их под «Yolutap nədir?», ТЗ владельца).
  const productsBlock = (
    <>
    {/* продукты */}
    <section className="hp-wrap hp-section hp-products">
      <div className="hp-head">
        <h2 className="hp-h2">{t.one_approach_for_families_and_for_schools}</h2>
        <p className="hp-lead">{t.for_families_understand_your_child_and_plan_the_}</p>
      </div>
      <div ref={grid}>
        <div id="families" className="hp-cards" style={{ scrollMarginTop: 80 }}>
          <a href={`${start}&pkg=basic`} data-prod className="hp-card" data-cta="card_basic">
            <div className="hp-card__top">
              <span style={{ color: "var(--color-teal)" }}>{t.families_ages_10_22}</span>
              <span className="hp-badge" style={{ background: "var(--color-teal-tint)", color: "var(--color-teal-ink)" }}>35 ₼</span>
            </div>
            <span className="hp-card__title">{t.career_profile}</span>
            <span className="hp-card__text">{t.a_test_of_about_30_minutes_and_a_10_page_report_}</span>
            <div className="hp-card__visual" aria-hidden="true">
              <i style={{ width: "88%", background: "var(--color-teal)" }} />
              <i style={{ width: "70%", background: "var(--color-green)" }} />
              <i style={{ width: "54%", background: "var(--color-purple)" }} />
              <i style={{ width: "28%", background: "#C8D0D6" }} />
            </div>
            <span className="hp-card__go" style={{ color: "var(--color-teal)" }}>{t.take_the_test}</span>
          </a>
          <a href={`${start}&pkg=premium`} data-prod className="hp-card" data-cta="card_premium">
            <div className="hp-card__top">
              <span style={{ color: "var(--color-purple-deep)" }}>{t.families_with_a_parent}</span>
              <span className="hp-badge" style={{ background: "var(--color-purple-tint)", color: "#4A3478" }}>250 ₼</span>
            </div>
            <span className="hp-card__title">{t.profile_coach_session}</span>
            <span className="hp-card__text">{t.the_same_report_plus_60_minutes_with_a_career_co}</span>
            <div className="hp-coaches">
              <span className="hp-coaches__ph">
                <img src="/coaches/coach-nazilya-javanshir.jpg" alt="Nazilya Javanshir" width={48} height={48} loading="lazy" />
                <img src="/coaches/coach-yegana-seyidli-tufek.jpg" alt="Yegana Seyidli Tufek" width={48} height={48} loading="lazy" />
              </span>
              <span>{t.nazilya_javanshir_yegana_seyidli_tufek}</span>
            </div>
            <span className="hp-card__go" style={{ color: "var(--color-purple-deep)" }}>{t.choose_with_a_coach}</span>
          </a>
          <a href={STUDY_ABROAD_HREF[lang]} data-prod className="hp-card" data-cta="card_abroad">
            <div className="hp-card__top">
              <span style={{ color: "var(--color-green-deep)" }}>{t.families_grades_10_11}</span>
              <span className="hp-badge" style={{ background: "var(--color-green-tint)", color: "#1F5C47" }}>{t.free}</span>
            </div>
            <span className="hp-card__title">{t.study_abroad}</span>
            <span className="hp-card__text">{t.where_you_can_realistically_get_in_with_an_azerb}</span>
            <div className="hp-verdicts" aria-hidden="true">
              <small className="hp-verdicts__mark">{EXAMPLE_MARK[lang]}</small>
              <span><span>{t.university_netherlands}</span><span style={{ color: "#1F5C47" }}>{t.this_cycle}</span></span>
              <span><span>{t.university_germany}</span><span style={{ color: "var(--color-amber-text)" }}>{t.after_foundation}</span></span>
              <span><span>{t.university_uk}</span><span style={{ color: "var(--color-ink-2)" }}>{t.next_year}</span></span>
            </div>
            <span className="hp-card__go" style={{ color: "var(--color-green-deep)" }}>
              {ABROAD_COPY[lang].cta} →
            </span>
          </a>
        </div>

        <div id="schools" className="hp-schools">
          <div className="hp-schools__photo">
            <Photo name="eas-teacher-help" alt={t.a_teacher_helps_a_student_during_the_yolutap_tes} sizes="(max-width: 980px) 100vw, 45vw" />
            <span className="hp-schools__tag">
              <EasMark size={32} lang={lang} />
              {EAS}
            </span>
          </div>
          <div className="hp-schools__body">
            <span className="hp-schools__label">{t.schools}</span>
            <div className="hp-schools__cards">
              <a href="#contact" data-prod className="hp-card hp-card--dark" data-cta="card_school_career">
                <div className="hp-card__top">
                  <span>{t.grades_7_11}</span>
                  <span className="hp-badge">{t["25_student"]}</span>
                </div>
                <span className="hp-card__title">{t.career_guidance_for_classes}</span>
                <span className="hp-card__text">{t.a_science_based_profile_for_a_whole_year_group_a}</span>
                <span className="hp-card__go">{t.discuss_for_your_school}</span>
              </a>
              <a href="#contact" data-prod className="hp-card hp-card--dark" data-cta="card_school_climate">
                <div className="hp-card__top">
                  <span>{t.grades_4_11}</span>
                  <span className="hp-badge">{t["8_student_year"]}</span>
                </div>
                <span className="hp-card__title">{t.school_climate}</span>
                <span className="hp-card__text">{t.an_anonymous_15_minute_survey_on_class_climate_a}</span>
                <span className="hp-card__go">{t.first_round_free}</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="hp-careers">
        <div className="hp-careers__copy">
          <h2>{t.careers_strip_title}</h2>
          <p>{t.careers_strip_text}</p>
        </div>
        <a className="hp-btn hp-btn--outline" href={`${pre}/careers`} data-cta="careers_map">
          {t.careers_strip_cta}
        </a>
      </div>
    </section>
    </>
  );
  const bandBlock = (
    <>
    {/* документальная полоса EAS */}
    <section className="hp-band" ref={band} aria-label={t.yolutap_at_european_azerbaijan_school}>
      <div ref={bandImg} style={{ position: "absolute", inset: 0 }}>
        <Photo name="eas-class-wide" alt={t.a_class_at_european_azerbaijan_school_taking_the} sizes="100vw" />
      </div>
      <div className="hp-band__wrap">
        <figure>
          <blockquote lang="en">{QUOTE}</blockquote>
          <figcaption>
            <span className="hp-eas">
              <EasMark size={48} lang={lang} />
              <b>{EAS}</b>
            </span>
            <span>{t.grades_7_10_one_of_baku_s_leading_ib_schools}</span>
          </figcaption>
        </figure>
      </div>
    </section>
    </>
  );
  const testBlock = (
    <>
    {/* тест */}
    <section id="test" className="hp-wrap hp-section hp-test">
      <div className="hp-test__media">
        <div className="hp-test__big">
          <Photo name="eas-test-intro-screen" alt={t.the_test_s_first_screen_let_s_make_a_deal_there_} sizes="(max-width: 980px) 82vw, 40vw" style={{ objectPosition: "60% 50%" }} />
        </div>
        <div className="hp-test__small">
          <Photo name="eas-test-question-screen" alt={t.a_test_question_about_how_a_student_approaches_a} sizes="(max-width: 980px) 50vw, 25vw" style={{ objectPosition: "55% 40%" }} />
        </div>
      </div>
      <div className="hp-test__copy">
        <h2 className="hp-h2">{t.a_test_that_speaks_a_teenager_s_language}</h2>
        <p className="hp-lead">{t.the_first_thing_a_student_sees_is_a_deal_there_a}</p>
        <dl className="hp-dl">
          <div><dt>{t.about_30_minutes}</dt><dd>{t.you_can_pause_and_come_back}</dd></div>
          <div><dt>{t.age_adapted}</dt><dd>{t.five_age_groups_from_10_to_22}</dd></div>
          <div><dt>{t.three_theories}</dt><dd>{t.super_holland_and_piaget_ai_applies_them_rather_}</dd></div>
          <div><dt>{t.three_languages}</dt><dd>{t.azerbaijani_russian_english}</dd></div>
        </dl>
        <a href={SAMPLE_PDF[lang]} className="hp-textbtn" style={{ fontWeight: 800 }} data-cta="sample_pdf">
          {t.download_a_sample_report}
        </a>
      </div>
    </section>
    </>
  );
  const familyBlock = (
    <>
    {/* путь семьи */}
    <section className="hp-cream">
      <div className="hp-wrap hp-section">
        <div className="hp-family">
          <div className="hp-family__photo">
            <Photo name="gen-family-kitchen" alt={t.a_mother_and_her_teenage_son_go_through_a_printe} sizes="(max-width: 980px) 100vw, 55vw" />
          </div>
          <div className="hp-family__copy">
            <h2 className="hp-h2">{t.first_where_it_s_worth_going_then_where_it_s_rea}</h2>
            <p className="hp-lead">{t.the_report_arrives_by_email_so_you_can_go_throug}</p>
          </div>
        </div>
        <div className="hp-steps">
          <div className="hp-step">
            <i style={{ background: "var(--color-teal)" }} />
            <span className="hp-step__label" style={{ color: "var(--color-teal)" }}>{t.career_profile}</span>
            <span className="hp-step__title">{t.who_your_child_is_and_what_fits_them}</span>
            <span className="hp-step__text">{t.archetype_strengths_and_three_directions_in_word}</span>
          </div>
          <div className="hp-step">
            <i style={{ background: "var(--color-green)" }} />
            <span className="hp-step__label" style={{ color: "var(--color-green-deep)" }}>{t.study_abroad}</span>
            <span className="hp-step__title">{t.where_it_s_taught_and_whether_they_can_get_in}</span>
            <span className="hp-step__text">{t.an_honest_verdict_for_every_university_this_cycl}</span>
          </div>
          <div className="hp-step">
            <i style={{ background: "var(--color-purple)" }} />
            <span className="hp-step__label" style={{ color: "var(--color-purple-deep)" }}>{t.coach_session}</span>
            <span className="hp-step__title">{t.talk_it_all_through_together}</span>
            <span className="hp-step__text">{t["60_minutes_with_a_coach_child_and_parent_togethe"]}</span>
          </div>
        </div>
      </div>
    </section>
    </>
  );

  return (
    <div className="hp">
      <AuthErrorBar lang={lang} />
      <SiteHeader lang={lang} home />
      <main>
        {hero === "pins" ? (
          <>
            <HomePins lang={lang} countries={countries} />
            <PinsAbout lang={lang} countries={countries} />
            {/* #check — на него ведут /test и реклама (?mini=1): проверка остаётся. Один заголовок (27.10.2026). */}
            <section className="hp-wrap hp-section pa-check" style={{ paddingTop: 24 }}>
              <div className="pa-check__copy">
                <h2 className="hp-h2">{t.free_check}</h2>
              </div>
              <FreeCheck lang={lang} t={t} stepOnly />
            </section>
            {/* Порядок — ТЗ владельца 27.10.2026, без повторов: пины → «Yolutap nədir?» →
                проверка → тест с фото → «Bütün qiymətlər» (одна таблица) → отзывы →
                «Məktəblərə →» → EAS → FAQ → форма. Карточки «Bir yanaşma» (семьи и школы)
                сняты: те же продукты и цены — в таблице. */}
            {testBlock}
            {familyBlock}
            <PricesSection t={t} prices={prices} more={more} />
            <Reviews lang={lang} />
            <SchoolsLink lang={lang} />
            {bandBlock}
            <HomePopular lang={lang} data={popular} />
            <FaqSection k={k} />
            <Contact lang={lang} />
          </>
        ) : (
        <>
        {/* первый экран — см. HOME_HERO */}
        {hero === "doors" ? (
          <HomeDoors
            lang={lang}
            proof={t.this_year_grades_7_10_at_european_azerbaijan_sch}
            freeLink={t.or_a_free_one_minute_check}
            freeCheck={<FreeCheck lang={lang} t={t} />}
          />
        ) : (
          <LegacyHero lang={lang} t={t} start={start} photoRef={heroPhoto} />
        )}

        {productsBlock}
        {bandBlock}
        {testBlock}
        {familyBlock}

        <Reviews lang={lang} />

        <PricesSection t={t} prices={prices} more={more} />
        <HomePopular lang={lang} data={popular} />
        <FaqSection k={k} />

        <Guide lang={lang} />
        <Contact lang={lang} />

        {/* финал */}
        <section className="hp-wrap hp-section hp-final">
          <div className="hp-final__card">
            <div className="hp-final__copy">
              <h2>
                {t.give_your_child_words_for_who_they_are}{" "}
                <span className="hp-serif">{t.while_there_s_still_time_to_choose}</span>
              </h2>
              <div className="hp-row">
                <a href="#check" className="hp-btn hp-btn--lg" data-cta="final_check">
                  {t.free_check}
                </a>
                <a href="#schools" className="hp-final__school" data-cta="final_school">
                  {t.i_represent_a_school}
                </a>
              </div>
            </div>
            <div className="hp-final__photo">
              <Photo name="eas-class-teacher" alt={t.a_teacher_and_students_during_the_test_in_class} sizes="(max-width: 980px) 100vw, 45vw" />
            </div>
          </div>
        </section>
        </>
        )}
      </main>

      <SiteFooter lang={lang} home />
    </div>
  );
}

/** Мини-тест главной для страницы бесплатного теста (/besplatnyy-test-proforientatsii и др.) — тот же блок. */
export function FreeCheckBlock({ lang }: { lang: HomeLang }) {
  return <FreeCheck lang={lang} t={COPY[lang]} />;
}
